import type { ReactNode } from 'react'

import { AppHeader } from '@/components/layout/AppHeader'
import { Kbd } from '@/components/ui/Kbd'
import { cn } from '@/lib/utils'
import type { OnboardingState } from '@/types/api'

interface WizardShellProps {
  state: OnboardingState
  eyebrow: string
  title: string
  subtitle?: string
  children: ReactNode
  onBack?: () => void
  canGoBack?: boolean
}

/**
 * The chrome around every onboarding step.
 *
 * A slim top bar (logo, "Step x of n", a progress line across the full
 * width, the account), the step centred at 640px, and a quiet footnote with
 * the resume status. Steps supply only their fields and a `WizardActions`
 * bar, so spacing stays identical across all six screens.
 */
export function WizardShell({
  state,
  eyebrow,
  title,
  subtitle,
  children,
  onBack,
  canGoBack = true,
}: WizardShellProps) {
  const percent = (state.currentIndex / state.totalSteps) * 100
  const stepLabel = `Step ${state.currentIndex} of ${state.totalSteps}`

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <AppHeader
        progress={percent}
        progressLabel={stepLabel}
        progressNow={state.currentIndex}
        progressMax={state.totalSteps}
      >
        <p className="truncate text-[13px] text-ink-muted">
          Step{' '}
          <span className="num font-medium text-ink">{state.currentIndex}</span>
          {' '}of{' '}
          <span className="num">{state.totalSteps}</span>
        </p>
      </AppHeader>

      {/* Focused fields scroll clear of the sticky action bar on phones. */}
      <div className="mx-auto w-full max-w-[700px] flex-1 px-4 pb-8 pt-4 sm:px-6 sm:pt-8 md:pb-12 [&_input]:scroll-mb-36 [&_textarea]:scroll-mb-36">
        <main className="sm:card sm:px-7 sm:py-7 md:px-10 md:py-9">
          {canGoBack && onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="-ml-2 mb-2 inline-flex h-11 items-center gap-1.5 rounded-field px-2 text-[13.5px] font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink md:mb-4"
            >
              <span aria-hidden="true">←</span> Back
            </button>
          ) : (
            <div className="h-2 md:h-0" aria-hidden="true" />
          )}

          <p className="label-eyebrow text-brand">{eyebrow}</p>
          <h1 className="mt-2.5 text-balance text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[30px]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 text-[15px] leading-relaxed text-ink-muted sm:text-[16px]">
              {subtitle}
            </p>
          )}

          <div className="mt-7 animate-fade-in sm:mt-9">{children}</div>
        </main>

        <Footnote state={state} />
      </div>
    </div>
  )
}

/**
 * The step's buttons. On phones a sticky bar at the foot of the screen
 * (position sticky, so the keyboard pushes it up rather than it covering
 * the focused input); from `md` it sits inline under the fields.
 */
export function WizardActions({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div
      className={cn(
        'action-bar mt-8 flex flex-col gap-3 [bottom:var(--safe-bottom)] sm:-mx-6',
        'md:static md:mx-0 md:mt-10 md:flex-row md:items-center md:justify-end md:rounded-none md:border-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-none',
      )}
    >
      {aside && <div className="text-[13px] text-ink-muted md:mr-auto">{aside}</div>}
      <div className="flex items-center gap-3">{children}</div>
    </div>
  )
}

/** A bordered group of questions with an eyebrow title. */
export function WizardPanel({
  title,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) {
  return (
    <section className={cn('panel overflow-hidden', className)}>
      {title && (
        <header className="border-b border-line bg-surface-muted/70 px-4 py-2.5 sm:px-5">
          <h2 className="label-eyebrow">{title}</h2>
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  )
}

/** One question: label stacked above its control on phones, side by side from `sm`. */
export function QuestionRow({
  label,
  hint,
  children,
  className,
  last,
}: {
  label: string
  hint?: string
  children: ReactNode
  className?: string
  last?: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-5',
        !last && 'border-b border-line',
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-[15px] text-ink">{label}</p>
        {hint && <p className="mt-0.5 text-[13px] text-ink-muted">{hint}</p>}
      </div>
      <div className="w-full sm:w-auto sm:shrink-0">{children}</div>
    </div>
  )
}

/**
 * Makes a `Segmented` / `YesNo` fill the row on phones (each option an equal
 * share, 44px tall) and go back to its natural width from `sm`.
 */
export function ChoiceWrap({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'w-full [&>div]:flex [&>div]:w-full [&>div>button]:min-h-[44px] [&>div>button]:flex-1',
        'sm:w-auto sm:[&>div]:inline-flex sm:[&>div]:w-auto sm:[&>div>button]:min-h-[36px] sm:[&>div>button]:flex-none',
        className,
      )}
    >
      {children}
    </div>
  )
}

function Footnote({ state }: { state: OnboardingState }) {
  const parsed = state.resumeStatus === 'parsed'
  const failed = state.resumeStatus === 'failed'
  const missing = state.resumeStatus === 'missing'

  const status = failed ? 'needs attention' : missing ? 'not uploaded' : parsed ? 'done' : 'in progress'
  const message = failed
    ? "We couldn't read that file. You can re-upload it later from your profile."
    : missing
      ? 'Add one from your profile later and we will prefill the rest for you.'
      : parsed
        ? "Parsed. While you finish setup, we're matching jobs for you."
        : "Takes 30–60 seconds. We're pulling your work history and skills while you finish these."

  return (
    <aside className="mt-6 grid gap-5 border-t border-line pt-5 text-[13px] text-ink-muted sm:grid-cols-2 sm:gap-8 md:mt-8 md:border-0 md:px-10 md:pt-2">
      <div>
        <p className="flex items-center gap-2 font-medium text-ink-soft">
          <span
            className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-full',
              failed ? 'bg-danger' : missing ? 'bg-line-strong' : parsed ? 'bg-brand' : 'animate-pulse bg-brand',
            )}
            aria-hidden="true"
          />
          Resume · {status}
        </p>
        <p className="mt-1 leading-relaxed">{message}</p>
        {!missing && !parsed && !failed && (
          <div className="mt-2 h-1 w-full max-w-[220px] overflow-hidden rounded-pill bg-line">
            <div
              className="h-full rounded-pill bg-brand transition-[width] duration-700 ease-out"
              style={{ width: `${Math.max(state.resumeProgress, 8)}%` }}
            />
          </div>
        )}
      </div>
      <div>
        <p className="font-medium text-ink-soft">Why we ask</p>
        <p className="mt-1 leading-relaxed">Every application asks these. Answer once here, we fill the forms.</p>
        <p className="mt-2 hidden items-center gap-x-4 gap-y-1 [@media(hover:hover)]:flex">
          <span className="inline-flex items-center gap-1.5">
            <Kbd keys="⏎" /> Continue
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Kbd keys={['⇧', 'Tab']} /> Back
          </span>
        </p>
      </div>
    </aside>
  )
}
